/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useState } from 'react'
import { useEffect } from 'react'
import { trackEvent } from 'lib/tracking/analytics-service'
import { toast } from 'react-toastify'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducer'
import {
    AdditionalSignUpAttributes,
    getDefaultAdditionalSignUpAttributes,
    LoginSteps,
} from 'lib/tracking/attribution/signupattributes'
import { AnalyticsEventSignUpImpression, AnalyticsEventUserCreate } from 'lib/constants/AnalyticsEventType'
import { useRouter } from 'next/router'
import AuthGoogle from 'components/feature/Authentication/AuthGoogle'
import AuthApple from 'components/feature/Authentication/AuthApple'
import AuthEmail from 'components/feature/Authentication/AuthEmail'
import styled from 'styled-components'
import {
    appFeedsRoute,
    appMyKnowsRoute,
    appOnboardingRouteOptions,
    legalPrivacyRoute,
    legalTOSRoute,
    signInRoute,
} from 'lib/constants/routes'
import { parseLocale } from 'lib/constants/localization'
import { getQueryParamRouter, getReferrerScreen } from 'lib/formatting/url'
import { parseTranslationInterpolation } from 'lib/features/translation'
import _useTranslation from 'lib/hooks/_useTranslation'
import { Body2, ButtonText, Caption } from 'stories/elements/Typography/Text'
import theme from 'stories/utils/theme'
import { TextSeparator } from 'components/elements/TextSeparator'
import Stack from 'components/elements/Stack'
import NewButton from 'components/elements/NewButton'
import { useToggleState } from 'lib/hooks/useToggleState'
import { getSignUpAndSignInDefaultRedirectPath } from 'lib/features/auth'
import { getCountryConfig } from 'lib/constants/countryconfig'
import { isDeviceMobile } from 'lib/features/deviceInfo'
import EmailIcon from 'assets/icons/mail.svg'
import { getCountryLanguageConfig } from 'lib/constants/countryLanguageConfig'
import { isExpansionContentCompetitionLive } from 'lib/features/expansion'
import { CountryCode } from 'interfaces/Country'

const LegalArticleLink = styled.a`
    && {
        width: 100%;
        text-align: center;
        font-family: var(--font-inter);
        font-size: 14px;
        font-weight: 500;
        line-height: 24px;
        color: ${theme.colors.accentBlueContrast};
        cursor: pointer;
    }
`

const ContinueButtonWrapper = styled.div`
    height: 200px;
    svg {
        fill: ${theme.colors.primaryGray};
    }
`

const LegalText = styled(Caption)`
    font-family: var(--font-inter) !important;
    font-weight: 500;
    line-height: 14px;
    * {
        line-height: 14px !important;
        font-weight: 400;
        font-size: 12px !important;
        font-family: var(--font-inter) !important;
    }
`

interface Props {
    onSuccessSignUp?: () => void
    removeMargins?: boolean
    showToggleEmailFormButton?: boolean
    isDarkTheme?: boolean
}

function SignUpFlow({ onSuccessSignUp, removeMargins, showToggleEmailFormButton, isDarkTheme }: Props) {
    const [additionalSignUpAttributes, setAdditionalSignUpAttributes] = useState<AdditionalSignUpAttributes>()
    const [showEmailForm, toggleShowEmailForm] = useToggleState(!showToggleEmailFormButton)
    const router = useRouter()
    const parsedLocale = parseLocale(router.locale)
    const countryLanguageConfig = getCountryLanguageConfig(router.locale)
    const { hidePayouts } = getCountryConfig(parsedLocale.countryCode)
    const isExpansion = isExpansionContentCompetitionLive(countryLanguageConfig)
    const authenticatedUser = useSelector((state: AppState) => state.user)
    const authenticatedKnower = useSelector((state: AppState) => state.knower)
    const referrerScreen = getReferrerScreen(router)
    const redirect = getQueryParamRouter(router, 'redirect')
    const { t } = _useTranslation('auth')
    const { t: tCommon } = _useTranslation('common')
    const isMobile = isDeviceMobile()

    const showWebappConversionEntryPoint = !!(authenticatedUser && !isMobile) && !isExpansion

    useEffect(() => {
        if (authenticatedUser !== null) {
            if (parsedLocale.countryCode === CountryCode.Germany) {
                router.push(appOnboardingRouteOptions({ referrerScreen, redirect: redirect }))
            } else if (showWebappConversionEntryPoint) {
                router.push(getSignUpAndSignInDefaultRedirectPath(showWebappConversionEntryPoint))
            } else {
                router.push(authenticatedKnower ? appMyKnowsRoute : appFeedsRoute)
            }
            return
        }

        trackEvent(AnalyticsEventSignUpImpression, { referrerScreen })
        const attributes = getDefaultAdditionalSignUpAttributes(parsedLocale.countryCode, parsedLocale.interfaceLanguageCode)
        setAdditionalSignUpAttributes(attributes)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        router,
        authenticatedUser,
        authenticatedKnower,
        parsedLocale.countryCode,
        parsedLocale.interfaceLanguageCode,
        referrerScreen,
        showWebappConversionEntryPoint,
        isExpansion,
        hidePayouts,
    ])

    const handleSuccess = (isNewAccount: boolean, type: 'email' | 'google' | 'apple') => {
        if (isNewAccount) {
            trackEvent(AnalyticsEventUserCreate, { value: type, referrerScreen })
            toast.success(t('auth/signUpRegisterSuccessMessage'))
        }

        onSuccessSignUp && onSuccessSignUp()
    }

    return (
        <>
            {additionalSignUpAttributes ? (
                <div>
                    <Stack gutter={removeMargins ? 0 : 10}>
                        <AuthGoogle
                            type={LoginSteps.SignUp}
                            additionalSignUpAttributes={additionalSignUpAttributes}
                            onClick={() => {}}
                            onSuccess={(isNewAccount) => handleSuccess(isNewAccount, 'google')}
                            handleNoAccount={() => {}}
                        />

                        <AuthApple
                            type={LoginSteps.SignUp}
                            additionalSignUpAttributes={additionalSignUpAttributes}
                            onClick={() => {}}
                            onSuccess={(isNewAccount) => handleSuccess(isNewAccount, 'apple')}
                            handleNoAccount={() => {}}
                        />
                    </Stack>
                    <TextSeparator noMargin={removeMargins} isDarkTheme={isDarkTheme}>
                        <span />
                        <p>{t('auth/signUpOderLabel')}</p>
                        <span />
                    </TextSeparator>
                    <Stack gutter={30}>
                        {!showEmailForm ? (
                            <ContinueButtonWrapper>
                                <NewButton
                                    fullWidth
                                    color={theme.colors.primaryGray}
                                    icon={<EmailIcon />}
                                    borderRadius={theme.borderRadius.large}
                                    height={50}
                                    width={300}
                                    type="secondary"
                                    onClick={toggleShowEmailForm}
                                >
                                    <ButtonText color={theme.colors.primaryGray}>{tCommon('common/continue')}</ButtonText>
                                </NewButton>
                            </ContinueButtonWrapper>
                        ) : (
                            <Stack>
                                <AuthEmail
                                    type={LoginSteps.SignUp}
                                    additionalSignUpAttributes={additionalSignUpAttributes}
                                    onClick={() => {}}
                                    onSuccess={(isNewAccount) => handleSuccess(isNewAccount, 'email')}
                                    removeMargins={removeMargins}
                                    isDarkTheme={isDarkTheme}
                                />
                            </Stack>
                        )}

                        <Body2 textAlign="center">
                            <LegalArticleLink href={signInRoute({ referrerScreen, redirect })}>
                                {t('auth/signUpAlreadyHaveAnAccount')}
                            </LegalArticleLink>
                        </Body2>
                        <LegalText marginBottom={20} textAlign="center" color={theme.colors.grayScaleSecondary}>
                            {parseTranslationInterpolation(t('auth/signUpRegisterLegalMessage'), [
                                { index: 1, component: LegalArticleLink, props: { href: legalTOSRoute } },
                                { index: 3, component: LegalArticleLink, props: { href: legalPrivacyRoute } },
                            ])}
                        </LegalText>
                    </Stack>
                </div>
            ) : null}
        </>
    )
}

export default SignUpFlow
